import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Check, Copy, ExternalLink } from 'lucide-react';

interface DirectMessagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId?: string;
  phoneNumber?: string;
  emailAddress?: string;
  currentLanguage?: string;
}

// Official WhatsApp Logo
export function WhatsAppIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill="#25D366"/>
      <path fill="#FFFFFF" d="M12 3.5a8.5 8.5 0 0 0-7.35 12.75L3.5 20.5l4.35-1.14A8.5 8.5 0 1 0 12 3.5zm4.84 12c-.2.57-1.17 1.08-1.63 1.15-.42.06-.97.12-2.8-.62-2.34-.95-3.84-3.32-3.96-3.48-.11-.15-.96-1.27-.96-2.43 0-1.15.6-1.72.82-1.95.21-.23.47-.29.63-.29.16 0 .32 0 .46.01.15.01.35-.06.55.42.21.5.71 1.73.77 1.86.06.13.1.28.02.44-.08.16-.13.26-.25.4-.12.14-.26.31-.37.42-.12.12-.25.25-.11.49.14.24.63 1.04 1.36 1.69.93.83 1.72 1.09 1.96 1.21.24.12.38.1.52-.06.14-.16.6-.7.76-.94.16-.24.32-.2.54-.12.22.08 1.41.66 1.65.78.24.12.4.18.46.28.06.1.06.58-.14 1.15z"/>
    </svg>
  );
}

// Official Viber Logo
export function ViberIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="12" fill="#7360F2"/>
      <path fill="#FFFFFF" fillRule="evenodd" clipRule="evenodd" d="M11.95 4C7.56 4 4 7.21 4 11.17c0 2.22 1.13 4.2 2.91 5.48l-.75 2.25c-.1.31.22.58.5.42l2.67-1.5c.84.23 1.73.35 2.62.35 4.39 0 7.95-3.21 7.95-7.17S16.34 4 11.95 4zm4.27 10.42c-.22.37-.88.67-1.38.68-.45.01-.98-.12-2.83-1.02-2.37-1.15-3.88-3.56-4-3.72-.12-.16-.98-1.31-.98-2.5 0-1.19.62-1.77.84-2.01.22-.24.48-.3.64-.3.16 0 .32 0 .46.01.15.01.35-.06.55.43.21.51.72 1.78.78 1.91.06.13.1.28.02.45-.08.17-.13.27-.26.41-.13.14-.27.32-.38.43-.12.12-.25.26-.11.5.14.24.64 1.07 1.39 1.74.96.86 1.77 1.12 2.02 1.25.25.12.39.1.53-.06.14-.16.62-.72.78-.97.16-.25.33-.21.55-.13.22.08 1.45.68 1.7.8.25.12.41.18.47.28.06.1.06.59-.16 1.18z"/>
    </svg>
  );
}

// Official Telegram Logo
export function TelegramIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="12" fill="#2AABEE"/>
      <path fill="#FFFFFF" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.67-.52.36-.99.53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.24.38-.49 1.07-.75 4.19-1.82 6.99-3.02 8.4-3.6 4-.17 4.83.52 4.8 2.84z"/>
    </svg>
  );
}

// Official Text Messaging (SMS) Logo
export function TextMessagingIcon({ className = "w-7 h-7" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#007AFF"/>
      <path fill="#FFFFFF" d="M12 4.5c-4.42 0-8 3.13-8 7 0 1.83.82 3.5 2.15 4.73C5.6 17.58 4.7 18.8 3.5 19.5c2.1 0 4.1-.75 5.5-1.7 1 .28 2 .4 3 .4 4.42 0 8-3.13 8-7s-3.58-7-8-7z"/>
      <circle cx="8.5" cy="11.5" r="1.1" fill="#007AFF"/>
      <circle cx="12" cy="11.5" r="1.1" fill="#007AFF"/>
      <circle cx="15.5" cy="11.5" r="1.1" fill="#007AFF"/>
    </svg>
  );
}

const modalTranslations: Record<string, {
  headerTitle: string;
  headerSub: string;
  bookingRef: string;
  defaultText: (bookingId: string) => string;
  whatsappDesc: string;
  whatsappAction: string;
  viberDesc: string;
  viberAction: string;
  telegramDesc: string;
  telegramAction: string;
  smsDesc: string;
  smsAction: string;
  smsName: string;
  templateLabel: string;
  copyText: string;
  copiedText: string;
  directPhone: string;
  close: string;
}> = {
  en: {
    headerTitle: "Direct Messaging Options",
    headerSub: "Select your preferred messenger app to contact Cozzydine Administration with your Booking ID:",
    bookingRef: "Booking Reference ID:",
    defaultText: (id) => `Hello Cozzydine! I would like to confirm my booking (ID: ${id || 'B-3502'}) at Hostel Cozzydine.`,
    whatsappDesc: "Chat directly via WhatsApp",
    whatsappAction: "Open WhatsApp",
    viberDesc: "Send message via Viber",
    viberAction: "Open Viber",
    telegramDesc: "Instant chat on Telegram (@Cozzydine_Kyiv)",
    telegramAction: "Open Telegram",
    smsDesc: "Send direct SMS text message",
    smsAction: "Send SMS",
    smsName: "Text Messaging (SMS)",
    templateLabel: "Pre-filled confirmation message:",
    copyText: "Copy Text",
    copiedText: "Copied!",
    directPhone: "Direct Phone:",
    close: "Close"
  },
  uk: {
    headerTitle: "Оберіть Спосіб Зв’язку",
    headerSub: "Зв’яжіться з адміністрацією Cozzydine через зручний для вас месенджер для підтвердження бронювання:",
    bookingRef: "ID Бронювання:",
    defaultText: (id) => `Доброго дня! Я б хотів(ла) підтвердити моє бронювання (ID: ${id || 'B-3502'}) в Hostel Cozzydine.`,
    whatsappDesc: "Написати напряму у WhatsApp",
    whatsappAction: "Відкрити WhatsApp",
    viberDesc: "Написати у чат Viber",
    viberAction: "Відкрити Viber",
    telegramDesc: "Миттєве повідомлення у Telegram (@Cozzydine_Kyiv)",
    telegramAction: "Відкрити Telegram",
    smsDesc: "Надіслати стандартне SMS",
    smsAction: "Надіслати SMS",
    smsName: "Текстові повідомлення (SMS)",
    templateLabel: "Шаблон повідомлення:",
    copyText: "Скопіювати",
    copiedText: "Скопійовано!",
    directPhone: "Прямий телефон:",
    close: "Закрити"
  },
  es: {
    headerTitle: "Opciones de Mensajería Directa",
    headerSub: "Seleccione su aplicación de mensajería preferida para contactar a la Administración de Cozzydine con su ID de Reserva:",
    bookingRef: "ID de Referencia de Reserva:",
    defaultText: (id) => `¡Hola Cozzydine! Me gustaría confirmar mi reserva (ID: ${id || 'B-3502'}) en Hostel Cozzydine.`,
    whatsappDesc: "Chatea directamente por WhatsApp",
    whatsappAction: "Abrir WhatsApp",
    viberDesc: "Enviar mensaje por Viber",
    viberAction: "Abrir Viber",
    telegramDesc: "Chat instantáneo en Telegram (@Cozzydine_Kyiv)",
    telegramAction: "Abrir Telegram",
    smsDesc: "Enviar mensaje de texto SMS directo",
    smsAction: "Enviar SMS",
    smsName: "Mensajería de texto (SMS)",
    templateLabel: "Mensaje de confirmación predeterminado:",
    copyText: "Copiar Texto",
    copiedText: "¡Copiado!",
    directPhone: "Teléfono Directo:",
    close: "Cerrar"
  },
  fr: {
    headerTitle: "Options de Messagerie Directe",
    headerSub: "Sélectionnez votre application de messagerie préférée pour contacter l'administration de Cozzydine avec votre ID de réservation:",
    bookingRef: "ID de Référence de Réservation:",
    defaultText: (id) => `Bonjour Cozzydine ! Je souhaite confirmer ma réservation (ID: ${id || 'B-3502'}) à l'Auberge Cozzydine.`,
    whatsappDesc: "Discutez directement via WhatsApp",
    whatsappAction: "Ouvrir WhatsApp",
    viberDesc: "Envoyer un message via Viber",
    viberAction: "Ouvrir Viber",
    telegramDesc: "Chat instantané sur Telegram (@Cozzydine_Kyiv)",
    telegramAction: "Ouvrir Telegram",
    smsDesc: "Envoyer un SMS direct",
    smsAction: "Envoyer un SMS",
    smsName: "Messagerie texte (SMS)",
    templateLabel: "Message de confirmation pré-rempli :",
    copyText: "Copier le Texte",
    copiedText: "Copié !",
    directPhone: "Téléphone Direct :",
    close: "Fermer"
  }
};

export default function DirectMessagingModal({
  isOpen,
  onClose,
  bookingId = '',
  currentLanguage = 'en',
  phoneNumber = '+380 66 207 0475'
}: DirectMessagingModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const langKey = (currentLanguage in modalTranslations ? currentLanguage : 'en') as keyof typeof modalTranslations;
  const t = modalTranslations[langKey] || modalTranslations.en;

  const rawPhone = phoneNumber.replace(/[^0-9]/g, '');
  const defaultText = t.defaultText(bookingId);
  const encodedText = encodeURIComponent(defaultText);

  const handleCopyMessage = () => {
    try {
      navigator.clipboard.writeText(defaultText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      console.error('Failed to copy', e);
    }
  };

  const messagingOptions = [
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: <WhatsAppIcon className="w-8 h-8 shrink-0" />,
      desc: t.whatsappDesc,
      link: `https://wa.me/380662070475?text=${encodedText}`,
      actionLabel: t.whatsappAction,
      hoverBorder: 'hover:border-[#25D366]'
    },
    {
      id: 'viber',
      name: 'Viber',
      icon: <ViberIcon className="w-8 h-8 shrink-0" />,
      desc: t.viberDesc,
      link: `viber://chat?number=%2B${rawPhone}&text=${encodedText}`,
      actionLabel: t.viberAction,
      hoverBorder: 'hover:border-[#7360F2]'
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: <TelegramIcon className="w-8 h-8 shrink-0" />,
      desc: t.telegramDesc,
      link: `https://t.me/Cozzydine_Kyiv?text=${encodedText}`,
      actionLabel: t.telegramAction,
      hoverBorder: 'hover:border-[#229ED9]'
    },
    {
      id: 'sms',
      name: t.smsName,
      icon: <TextMessagingIcon className="w-8 h-8 shrink-0" />,
      desc: t.smsDesc,
      link: `sms:+${rawPhone}?body=${encodedText}`,
      actionLabel: t.smsAction,
      hoverBorder: 'hover:border-amber-400'
    }
  ];

  const handleMessengerClick = (opt: typeof messagingOptions[0], e: React.MouseEvent) => {
    if (opt.id === 'viber') {
      e.preventDefault();
      const viberProtocol = `viber://chat?number=%2B${rawPhone}&text=${encodedText}`;
      const viberWeb = 'https://viber.me/380662070475';

      try {
        window.location.href = viberProtocol;
      } catch (err) {
        console.warn('Could not launch Viber protocol directly:', err);
      }

      setTimeout(() => {
        window.open(viberWeb, '_blank', 'noopener,noreferrer');
      }, 300);
    }
  };

  return ReactDOM.createPortal(
    <div className="booking-modal-reset fixed inset-0 z-[999999] flex items-center justify-center p-3 sm:p-4 bg-zinc-950/85 backdrop-blur-md animate-fade-in">
      <div 
        className="relative w-[95vw] sm:w-[90vw] max-w-lg bg-zinc-900 border border-zinc-700/80 text-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden font-sans flex flex-col my-auto max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-zinc-800 flex justify-between items-start bg-gradient-to-r from-zinc-900 via-zinc-850 to-zinc-900">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping shrink-0" />
              <h3 className="text-sm sm:text-lg font-black uppercase font-mono tracking-wide text-white">
                {t.headerTitle}
              </h3>
            </div>
            <p className="text-[11px] sm:text-xs text-zinc-400 font-sans leading-relaxed">
              {t.headerSub}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 sm:p-2 text-zinc-400 hover:text-white bg-zinc-800/80 hover:bg-zinc-700 rounded-full transition-all cursor-pointer shrink-0 ml-2"
            aria-label="Close"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {bookingId && (
          <div className="px-4 sm:px-6 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center justify-between text-xs font-mono">
            <span className="text-amber-200/90 font-medium">
              {t.bookingRef}
            </span>
            <span className="text-amber-300 font-black px-2.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30">
              {bookingId}
            </span>
          </div>
        )}

        <div className="p-3 sm:p-6 space-y-2.5 sm:space-y-3 overflow-y-auto max-h-[55vh] sm:max-h-[60vh]">
          {messagingOptions.map((opt) => (
            <a
              key={opt.id}
              href={opt.link}
              onClick={(e) => handleMessengerClick(opt, e)}
              target={opt.id === 'sms' ? '_self' : '_blank'}
              rel="noopener noreferrer"
              className={`group flex items-center justify-between p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-zinc-800/90 border border-zinc-700/80 ${opt.hoverBorder} transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md`}
            >
              <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0 flex-1">
                <div className="p-1 rounded-xl bg-zinc-900 border border-zinc-700/50 shadow-inner group-hover:scale-105 transition-transform duration-200 shrink-0">
                  {opt.icon}
                </div>
                <div className="min-w-0 text-left flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-black text-white group-hover:text-amber-300 transition-colors">
                      {opt.name}
                    </span>
                  </div>
                  <p className="text-[10px] sm:text-[11px] text-zinc-400 truncate mt-0.5">
                    {opt.desc}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-zinc-900/90 border border-zinc-700 group-hover:bg-amber-400 group-hover:text-zinc-950 text-[10px] sm:text-xs font-bold font-mono transition-all shrink-0 ml-2">
                <span>{opt.actionLabel}</span>
                <ExternalLink className="w-3 h-3 sm:w-3.5 sm:h-3.5 opacity-70 group-hover:opacity-100" />
              </div>
            </a>
          ))}
        </div>

        <div className="p-3 sm:p-5 bg-zinc-950 border-t border-zinc-800 space-y-2.5 sm:space-y-3 text-left">
          <div className="p-2.5 sm:p-3 bg-zinc-900 rounded-xl border border-zinc-800 flex items-center justify-between gap-2.5 text-xs">
            <div className="min-w-0 flex-1 font-mono text-[10px] sm:text-[11px] text-zinc-300 truncate">
              <span className="text-zinc-500 uppercase text-[9px] block font-bold">
                {t.templateLabel}
              </span>
              "{defaultText}"
            </div>
            <button
              type="button"
              onClick={handleCopyMessage}
              className="flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-amber-300 rounded-lg text-[10px] sm:text-[11px] font-mono font-bold transition-all shrink-0 cursor-pointer border border-zinc-700"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{t.copiedText}</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  <span>{t.copyText}</span>
                </>
              )}
            </button>
          </div>

          <div className="flex justify-between items-center text-[10px] sm:text-[10.5px] text-zinc-400 font-mono">
            <span>📞 {t.directPhone} <strong className="text-white">{phoneNumber}</strong></span>
            <button
              type="button"
              onClick={onClose}
              className="text-zinc-500 hover:text-zinc-300 underline cursor-pointer"
            >
              {t.close}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
